import React, { useMemo } from 'react';
import { Avatar, Box, Link, Stack, Typography } from '@mui/joy';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { formatLogTimestamp } from '../../../helpers/utils';
import TextSelectionPopover from '../../../components/TextSelectionPopover';
import DocumentChip from '../../../components/DocumentChip';
import { parseMessageContent, PolicyChip, TaskChip } from './mentionUtils';

const UserMessage = ({ message, onQuote, onOpenDocument, onOpenPolicy, onOpenTask }) => {
  const contentSegments = useMemo(() => {
    return parseMessageContent(message.content);
  }, [message.content]);

  const handleDocumentClick = (segment) => {
    if (onOpenDocument) {
      onOpenDocument({
        id: segment.filePath,
        name: segment.fileName,
        path: segment.filePath,
        type: segment.fileName?.split('.').pop()?.toLowerCase(),
      });
    }
  };

  const renderContent = () => {
    const hasMentions = contentSegments.some(
      (s) => s.type === 'document' || s.type === 'policy' || s.type === 'task'
    );

    if (!hasMentions) {
      return message.content;
    }

    return contentSegments.map((segment, idx) => {
      if (segment.type === 'text') {
        return <span key={idx}>{segment.content}</span>;
      }
      if (segment.type === 'document') {
        return (
          <DocumentChip
            key={idx}
            document={{ name: segment.fileName, path: segment.filePath }}
            onClick={() => handleDocumentClick(segment)}
            size="sm"
            showOpenIcon={false}
          />
        );
      }
      if (segment.type === 'policy') {
        return (
          <PolicyChip
            key={idx}
            policyId={segment.policyId}
            onClick={() => onOpenPolicy?.(segment.policyId)}
          />
        );
      }
      if (segment.type === 'task') {
        return (
          <TaskChip
            key={idx}
            taskId={segment.taskId}
            onClick={() => onOpenTask?.(segment.taskId)}
          />
        );
      }
      return null;
    });
  };
  return (
    <Stack
      direction="row"
      gap={1.5}
      sx={{
        mb: 3,
        justifyContent: 'flex-end',
      }}
    >
      {/* Message Content */}
      <Box sx={{ maxWidth: '90%' }}>
      <TextSelectionPopover source="user" onQuote={onQuote}>
        <Box
          sx={{
            position: 'relative',
          }}
        >
          {/* Main Content */}
          <Box
            sx={{
              bgcolor: 'primary.softBg',
              borderRadius: 'lg',
              borderTopRightRadius: 4,
              px: 2.5,
              py: 1.5,
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <Typography
              level="body-md"
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                overflowWrap: 'break-word',
                wordBreak: 'normal',
                color: 'text.primary',
                lineHeight: 1.6,
                '& .MuiChip-root': {
                  verticalAlign: 'middle',
                  mx: 0.5,
                  my: 0.25,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                },
              }}
            >
              {renderContent()}
            </Typography>

            {/* Document Attachments */}
            {(message.fileName || message.file_name || message.document_url) && (
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'primary.100',
                }}
              >
                {/* Legacy file attachment display */}
                {(message.fileName || message.file_name) && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 'sm',
                        bgcolor: 'primary.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.600' }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="body-sm" sx={{ fontWeight: 500 }}>
                        {message.fileName || message.file_name}
                      </Typography>
                      {(message.fileSize || message.file_size) && (
                        <Typography level="body-xs" color="neutral">
                          {((message.fileSize || message.file_size) / 1024 / 1024).toFixed(2)} MB
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                )}

                {/* Document URL display */}
                {message.document_url && (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 'sm',
                        bgcolor: 'primary.100',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'primary.600' }} />
                    </Box>
                    <Link
                      href={message.document_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      level="body-sm"
                      endDecorator={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                      sx={{ fontWeight: 500 }}
                    >
                      View attached document
                    </Link>
                  </Stack>
                )}
              </Box>
            )}
          </Box>

          {/* Timestamp */}
          {message.timestamp && (
            <Typography
              level="body-xs"
              color="neutral"
              sx={{ mt: 0.5, textAlign: 'right', pr: 0.5 }}
            >
              {formatLogTimestamp(message.timestamp)}
            </Typography>
          )}
        </Box>
      </TextSelectionPopover>
      </Box>

      {/* User Avatar */}
      <Avatar
        size="sm"
        sx={{
          bgcolor: 'primary.100',
          color: 'primary.600',
          flexShrink: 0,
        }}
      >
        <PersonOutlineIcon sx={{ fontSize: 20 }} />
      </Avatar>
    </Stack>
  );
};

export default UserMessage;
